
import { useEffect, useState } from "react";
import { useEmailManagement } from "@/hooks/useEmailManagement";
import { useInvestorProcessing } from "@/hooks/useInvestorProcessing";
import EmailsList from "@/components/emails/EmailsList";
import EmailDetails from "@/components/emails/EmailDetails";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import EmailsDashboard from "@/components/emails/EmailsDashboard";

const SentEmails = () => {
  const { investors, contacts, loadSavedData } = useInvestorProcessing();
  const [dataLoaded, setDataLoaded] = useState(false);

  const {
    emails,
    emailStats,
    selectedEmail,
    loadEmailData,
    handleViewEmail,
    handleCloseEmailDetails,
    handleEmailUpdated,
    handleDeleteEmail,
  } = useEmailManagement();

  // Load data only once on initial mount
  useEffect(() => {
    if (!dataLoaded) {
      Promise.all([
        loadSavedData(),
        loadEmailData()
      ]).then(() => {
        setDataLoaded(true);
      });
    }
  }, [loadSavedData, loadEmailData, dataLoaded]);

  // Get email related data
  const getEmailRelatedData = () => {
    if (!selectedEmail) return { investor: undefined, contact: undefined };
    
    const investor = investors.find(inv => inv.id === selectedEmail.investorId);
    const contact = contacts.find(c => c.id === selectedEmail.contactId);
    
    return { investor, contact };
  };

  // Convert emailStats format to match EmailsDashboard expectations
  const convertedEmailStats = {
    totalSent: emailStats.totalSent || emailStats.sent || 0,
    totalResponses: emailStats.totalResponses || 0,
    positiveResponses: emailStats.positiveResponses || 0,
    responseRate: emailStats.responseRate || (
      emailStats.totalSent > 0 ? 
      ((emailStats.totalResponses || 0) / emailStats.totalSent) * 100 : 0
    )
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Investor Outreach Tracker</h1>

      {emails.length > 0 && (
        <EmailsDashboard stats={convertedEmailStats} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Outreach History</CardTitle>
          <CardDescription>
            Track and manage your investor outreach emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedEmail ? (
            <EmailDetails 
              email={selectedEmail}
              investor={getEmailRelatedData().investor}
              contact={getEmailRelatedData().contact}
              onClose={handleCloseEmailDetails}
              onEmailUpdated={handleEmailUpdated}
            />
          ) : emails.length > 0 ? (
            <EmailsList 
              emails={emails}
              investors={investors}
              contacts={contacts}
              onViewEmail={handleViewEmail}
              onDeleteEmail={handleDeleteEmail}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No emails have been tracked yet</p>
              <p className="text-sm mt-2">
                Generate an email for an investor and click "Mark as Sent" to track it here
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SentEmails;
